import polymorphism from './polymorphism.png'
import priceFunction from './price-function.png'
import interfaceImage from './interface.png'

const content = `<p>Polymorphism consists in expressing in a list of functions what a piece of code A needs from another piece of code B. B provides an implementation of those functions, and A calls them.</p>

<p>This allows any other piece of code to be used by A if it provides the same functions as B. And it allows any other code to use B if it relies on those functions.</p>

<p>This list of functions is called an Interface.</p>

<img src=${polymorphism} alt="polymorphism" />

<p>The code that calls the interface is called the users or clients of the interface (<code>A</code> and <code>A2</code> in the above schema) and the code that provides implementations of the functions of the interface are called the implementers of the interface (<code>B</code>, <code>B2</code> and <code>B3</code> in the above schema).</p>

<p>Polymorphism allows to achieve <a href="/low-coupling">Low coupling</a> because clients and implementers don’t know anything about each other. They only know the interface.</p>

<p>If something changes in the implementer, or if the implementer is replaced by another one, the client code doesn’t have to change as long as the interface doesn’t change. Similarly, if something changes in the client, or the client is replaced with another one, the code of the implementers doesn’t have to change.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate.</p>

<p>An online bookshop sells two types of books: paper books delivered by physical mail, and ebooks that users can download.</p>

<p>The codebase has a <code>PaperBook</code> class to represent a paper book and an <code>Ebook</code> class to represent an ebook. They’re very different classes and could even belong to different bounded contexts if the codebase uses <a href="/DDD">DDD</a>.</p>

<p>The bookshop assigns a price for each book, and that price can change over time:</p>

<img src=${priceFunction} alt="price-function" />

<p>Based on past prices of a book, the bookshop would like to be able to know the price trend for a given book. A price trend is defined with the following formula, identical for paper books and ebooks:</p>

<p>pricing_trend = 2 * (current_price - average_price) / (current_price_date - first_price_date)</p>

<p>To compute this trend we need the past prices of the book. But being in different contexts, paper books and ebooks have different ways to get the data of their past prices.</p>

<h2 id="Everything-in-the-same-code">Everything in the same code</h2>

<p>One option to implement price trends is to have one function like this:</p>

<pre><code class="language-ruby">def price_trend(book)
  past_prices = case book
    when PaperBook then # ...code to retrieve past prices of paper books
    when Ebook then # ...code to retrieve past prices of ebooks
    end
  
  average_price = past_prices.map(&amp;:price).sum.to_f / past_prices.count
  2 * (past_prices.last.price - average_price) / (past_prices.last.date - past_prices.first.date)
end</code></pre>

<p>The code retrieves past prices depending on the type of book, and the computes the price trend. But this means that the code of price trends knows about paper books, ebooks, and how to retrieve their specific past prices. This is a strong coupling. It implies that:</p>

<ul><li>if we change the way to retrieve past prices of paper books, the code of price trend has to change</li><li>if we change the way to retrieve past prices of ebooks, the code of price trend has to change</li><li>if we want to compute the price trend of something else that has past prices, the code of price trend has to change</li><li>if we want to compute something else than price trends that needs past prices, we have to rewrite there the lines that retrieve past prices</li></ul>

<p>Another option is to have the caller of <code>price_trend</code> pass the past prices:</p>

<pre><code class="language-ruby">def price_trend(past_prices)
  average_price = past_prices.map(&amp;:price).sum.to_f / past_prices.count
  2 * (past_prices.last.price - average_price) / (past_prices.last.date - past_prices.first.date)
end</code></pre>

<p>This solves the last one of the above problems but:</p>

<ul><li>the first 3 above problems apply now to the caller of <code>price_trend</code></li><li><code>price_trend</code> now reveals in its own interface that it uses past prices, which can be seen is a <a href="/encapsulation">leaky abstraction</a> as price trends are defined on books. If the formula changes and requires something else on the book, it makes the caller change.</li></ul>

<h2 id="Introducing-polymorphism">Introducing polymorphism</h2>

<p>Let’s introduce polymorphism in this code to fix the above problems.</p>

<p>We need an interface, that is a list of functions that callers and callees agree on. Here the caller is <code>price_trend</code>, the callees are the books, and the interface consists of only one function, <code>past_prices</code>, that provides the past prices.</p>

<img src=${interfaceImage} alt="interface" />

<p>An interface is like a contract on which the caller and the callee agree on. This contract contains the name of the functions, their parameters, and their return values.</p>

<p>In typed languages, such as C++, Java, C#, Scala and TypeScript, interfaces are written in code. In untyped languages such as Ruby, Python and JavaScript, interfaces are implicit. But in both cases they contain the same kind of information.</p>

<p>In our example, <code>PaperBook</code> can implement the interface by providing a method that respect the contract of the interface. That interface consists of one method called <code>past_prices</code> that takes no parameters and returns an array of numbers:</p>

<pre><code class="language-ruby">class PaperBook
  def past_prices
    # ...code to retrieve past prices of paper books
  end
end</code></pre>

<p>And similarly for <code>Ebook</code>:</p>

<pre><code class="language-ruby">class Ebook
  def past_prices
    # ...code to retrieve past prices of ebooks
  end
end</code></pre>

<p>Then <code>price_trend</code> can call the <code>past_prices</code> interface:</p>

<pre><code class="language-ruby">def price_trend(book)
  past_prices = book.past_prices
  average_price = past_prices.map(&amp;:price).sum.to_f / past_prices.count
  2 * (past_prices.last.price - average_price) / (past_prices.last.date - past_prices.first.date)
end</code></pre>

<p>Polymorphism allowed us to achieve <a href="/low-coupling">Low coupling</a> in the code as callers and callees know nothing about each other. They just know the interface. In practice this means that:</p>

<ul><li>if we change the way to retrieve past prices of paper books, the code of price trend doesn’t change</li><li>if we change the way to retrieve past prices of ebooks, the code of price trend doesn’t change</li><li>if we want to compute the price trend of something else that has past prices, it can just implement the interface by providing a <code>past_prices</code> method</li><li>if we want to compute something else than price trends that needs past prices, it can just call the interface and work with all books</li></ul>

<p>Polymorphism is a very common feature of software, and some languages such as <a href="https://www.youtube.com/watch?v=mU_n_ohIHQk">C++ offer many ways</a> to implement it beyond class methods.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Polymorphism consists in having callers and callee know about an interface but not about one another</li><li>Callers call the interface and callees implement the interface</li><li>Polymorphism allows to change the code of callers without changing the code of callees, and the other way around</li><li>It allows to swap out callees without changing the code of callers, and the other way around</li></ul>
</div>`

export default content
